import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "chat-history-container" }
const _hoisted_2 = { class: "padding-b-4" }
const _hoisted_3 = {
  key: 0,
  class: "text-align-left padding-b-2"
}
const _hoisted_4 = { class: "padding-b-4" }
const _hoisted_5 = {
  key: 0,
  class: "text-align-left padding-b-2"
}
const _hoisted_6 = { class: "padding-b-4" }
const _hoisted_7 = {
  key: 0,
  class: "text-align-left padding-b-2"
}
const _hoisted_8 = {
  key: 1,
  class: "no-history"
}
const _hoisted_9 = {
  key: 0,
  class: "text-align-center color-lighter"
}
const _hoisted_10 = {
  key: 1,
  class: "text-align-center color-lighter"
}
const _hoisted_11 = { class: "modal-content-wrapper" }

import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import FormButton from '@/components/common/button/FormButton.vue';
import CustomModal from '@/components/common/modal/CustomModal.vue';
import NavHeader from '@/components/common/nav-header/NavHeader.vue';
import TaskCard from '@/components/common/task-card/TaskCard.vue';
import { IndexIdentifiers } from '@/core/indexesEnum';
import {
  AssistantType,
  ChatAssistant,
  ReviewingAssistantType
} from '@/models/assistant-types';
import { Conversation } from '@/models/conversation';
import { IconName, IconSize } from '@/models/icon-types';
import navigationService from '@/services/NavigationService';
import ChatHistoryService from '@/services/ChatHistoryService';
import { getFormatedDate } from '@/utils/utils';
import { onMounted, ref } from 'vue';
import RouteNames from '@/configuration/router/names.routes';

const OLDER = 'older';
const TODAY = 'today';
const LAST7 = 'last7';


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatHistoryView',
  props: {
    assistant: {},
    currentConversationId: {}
  },
  setup(__props: any) {

const todayConversations = ref<Conversation[]>([]);
const last7daysConversations = ref<Conversation[]>([]);
const olderConversations = ref<Conversation[]>([]);
const loadingHistory = ref(true);

const showDeleteConfirmationModal = ref(false);
const pendingDeleteConversationId = ref<string>('');
const pendingDeleteAssistant = ref<ChatAssistant>();
const pendingDeleteListType = ref<string>('');

const props = __props;

onMounted(async () => {
  loadingHistory.value = true;
  await fetchAndGroupConversations();
  loadingHistory.value = false;
});

function toUTCDateKey(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

async function fetchAndGroupConversations(): Promise<void> {
  const conversationsObj = await ChatHistoryService.getUserConversations(
    props.assistant
  );

  const conversations = Object.values(conversationsObj)
    .map((c) => {
      const dateTime =
        (c as Conversation).dateTime ?? (c as { datetime?: string }).datetime;
      return { ...c, dateTime } as Conversation;
    })
    .sort(
      (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    );

  const todayKey = toUTCDateKey(new Date());

  todayConversations.value = conversations.filter(
    (c) => toUTCDateKey(new Date(c.dateTime)) === todayKey
  );

  last7daysConversations.value = conversations.filter((c) => {
    const diff =
      (todayKey - toUTCDateKey(new Date(c.dateTime))) / (1000 * 60 * 60 * 24);
    return diff >= 1 && diff <= 7;
  });

  olderConversations.value = conversations.filter((c) => {
    const diff =
      (todayKey - toUTCDateKey(new Date(c.dateTime))) / (1000 * 60 * 60 * 24);
    return diff > 7;
  });
}

async function selectConversation(id: string | undefined): Promise<void> {
  if (!id) return;
  navigationService.navigateTo({
    name: RouteNames.LegalAssistant,
    query: { conversationId: id }
  });
}

async function deleteConversation(
  id: string | undefined,
  assistant: ChatAssistant,
  listType: string
): Promise<void> {
  if (!id) return;
  pendingDeleteConversationId.value = id;
  pendingDeleteAssistant.value = assistant;
  pendingDeleteListType.value = listType;
  showDeleteConfirmationModal.value = true;
}

async function confirmDeleteConversation(): Promise<void> {
  showDeleteConfirmationModal.value = false;
  let listRef = null;
  if (pendingDeleteListType.value === TODAY) {
    listRef = todayConversations;
  } else if (pendingDeleteListType.value === LAST7) {
    listRef = last7daysConversations;
  } else if (pendingDeleteListType.value === OLDER) {
    listRef = olderConversations;
  }
  if (listRef) {
    removeConversationFromList(listRef, pendingDeleteConversationId.value);
  }
  try {
    await ChatHistoryService.deleteConversation(
      pendingDeleteConversationId.value
    );
  } catch (error) {
    console.error('Error deleting conversation:', error);
    await fetchAndGroupConversations();
  }
}

function removeConversationFromList(
  listRef: typeof todayConversations,
  conversationId: string
) {
  listRef.value = listRef.value.filter((c) => c.id !== conversationId);
}

function handleModalClose(): void {
  showDeleteConfirmationModal.value = false;
  pendingDeleteConversationId.value = '';
  pendingDeleteAssistant.value = undefined;
}

function getIconForAssistant(assistant: ChatAssistant): IconName {
  switch (assistant) {
    case AssistantType.LegalAssistant:
      return IconName.FileCloud;
    case ReviewingAssistantType.DocumentReviewingAssistant:
      return IconName.DocReview;
    default:
      return IconName.FileUser;
  }
}

function getConversationSubtitle(conversation: Conversation): string {
  const formattedDate = getFormatedDate(conversation.dateTime);

  if (conversation.ragIndex && IndexIdentifiers[conversation.ragIndex]) {
    return `${formattedDate} - ${IndexIdentifiers[conversation.ragIndex]}`;
  }

  return formattedDate;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(NavHeader),
    _createElementVNode("div", _hoisted_1, [
      (
        todayConversations.value.length ||
        last7daysConversations.value.length ||
        olderConversations.value.length
      )
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("div", _hoisted_2, [
              (todayConversations.value.length > 0)
                ? (_openBlock(), _createElementBlock("h5", _hoisted_3, _toDisplayString(_ctx.$t('legal_assistant.chat_history_today')), 1))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(todayConversations.value, (conversation, index) => {
                return (_openBlock(), _createBlock(TaskCard, {
                  key: index,
                  description: conversation.title!,
                  subtitle: getConversationSubtitle(conversation),
                  icon: getIconForAssistant(conversation.assistant),
                  "icon-size": _unref(IconSize).HeadlineM,
                  focused: conversation.id === _ctx.currentConversationId,
                  onClick: ($event: any) => (selectConversation(conversation.id!))
                }, {
                  actions: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: _unref(IconName).Trash,
                      size: _unref(IconSize).BodyL,
                      onClick: 
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    TODAY
                  )
              
                    }, null, 8, ["name", "size", "onClick"])
                  ]),
                  _: 2
                }, 1032, ["description", "subtitle", "icon", "icon-size", "focused", "onClick"]))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_4, [
              (last7daysConversations.value.length > 0)
                ? (_openBlock(), _createElementBlock("h5", _hoisted_5, _toDisplayString(_ctx.$t('legal_assistant.chat_history_last_7_days')), 1))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(last7daysConversations.value, (conversation, index) => {
                return (_openBlock(), _createBlock(TaskCard, {
                  key: index,
                  description: conversation.title!,
                  subtitle: getConversationSubtitle(conversation),
                  icon: getIconForAssistant(conversation.assistant),
                  "icon-size": _unref(IconSize).HeadlineM,
                  focused: conversation.id === _ctx.currentConversationId,
                  onClick: ($event: any) => (selectConversation(conversation.id!))
                }, {
                  actions: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: _unref(IconName).Trash,
                      size: _unref(IconSize).BodyL,
                      onClick: 
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    LAST7
                  )
              
                    }, null, 8, ["name", "size", "onClick"])
                  ]),
                  _: 2
                }, 1032, ["description", "subtitle", "icon", "icon-size", "focused", "onClick"]))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_6, [
              (olderConversations.value.length > 0)
                ? (_openBlock(), _createElementBlock("h5", _hoisted_7, _toDisplayString(_ctx.$t('legal_assistant.chat_history_last_30_days')), 1))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(olderConversations.value, (conversation, index) => {
                return (_openBlock(), _createBlock(TaskCard, {
                  key: index,
                  description: conversation.title!,
                  subtitle: getConversationSubtitle(conversation),
                  icon: getIconForAssistant(conversation.assistant),
                  "icon-size": _unref(IconSize).HeadlineM,
                  focused: conversation.id === _ctx.currentConversationId,
                  onClick: ($event: any) => (selectConversation(conversation.id!))
                }, {
                  actions: _withCtx(() => [
                    _createVNode(AppIcon, {
                      name: _unref(IconName).Trash,
                      size: _unref(IconSize).BodyL,
                      onClick: 
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    OLDER
                  )
              
                    }, null, 8, ["name", "size", "onClick"])
                  ]),
                  _: 2
                }, 1032, ["description", "subtitle", "icon", "icon-size", "focused", "onClick"]))
              }), 128))
            ])
          ], 64))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            (loadingHistory.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_ctx.$t('chat_history.loading')), 1))
              : (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_ctx.$t('chat_history.empty')), 1))
          ]))
    ]),
    _createVNode(CustomModal, {
      show: showDeleteConfirmationModal.value,
      "onUpdate:show": _cache[0] || (_cache[0] = (val) => (showDeleteConfirmationModal.value = val)),
      onClose: handleModalClose
    }, {
      "left-button": _withCtx(() => [
        _createVNode(FormButton, {
          label: _ctx.$t('common.confirm'),
          type: "primary",
          onClick: confirmDeleteConversation
        }, null, 8, ["label"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("h6", null, _toDisplayString(_ctx.$t('chat_history.delete_conversation_confirmation')), 1),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('chat_history.delete_conversation_warning')), 1)
        ])
      ]),
      _: 1
    }, 8, ["show"])
  ], 64))
}
}

})